import { Icon } from "@platform/ui/components";
import { focusRingStyles } from "@platform/ui/styles";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { VariantProps, tv } from "tailwind-variants";

interface FilterPopoverTriggerProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>, VariantProps<typeof trigger> {
  onClear?: () => void;
}
export const FilterPopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  FilterPopoverTriggerProps
>(({ className, onClear, status, children, ...rest }, ref) => {
  const { container } = trigger({ status });

  return (
    <div className="relative inline-block max-w-full">
      <PopoverPrimitive.Trigger asChild>
        <button {...rest} ref={ref} className={container({ className })}>
          <Icon
            className={twMerge("size-4 place-items-center", status === "active" ? "hidden" : "grid")}
            aria-hidden
            symbol="add-circle-line"
          />
          <span className="stack-x-2 items-center">{children}</span>
        </button>
      </PopoverPrimitive.Trigger>
      <button
        aria-label="Clear filters"
        onClick={() => {
          onClear?.();
        }}
        className={focusRingStyles({
          className: twMerge(
            "text-ds-text-primary inset-y-center hover:bg-ds-accent-primary-10 hover:text-ds-text-inverse absolute right-2 grid h-5 w-5 place-items-center rounded-md border-none bg-transparent p-0 outline-hidden transition-colors",
            status === "active" ? "grid" : "hidden",
          ),
        })}
      >
        <Icon aria-hidden className="size-4" symbol="close-line" />
      </button>
    </div>
  );
});
FilterPopoverTrigger.displayName = "FilterPopoverTrigger";

const trigger = tv({
  slots: {
    container: focusRingStyles({
      className:
        "text-sm border rounded-md h-[32px] px-3 py-1.5 transition-colors stack-x-1 items-center disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap",
    }),
  },
  variants: {
    status: {
      active: {
        container:
          "bg-ds-accent-primary-4 text-ds-text-primary border-transparent pr-[calc((--spacing(2)*2)+(--spacing(5)))] font-medium",
      },
      inactive: {
        container: "bg-ds-surface-2 border-dashed border-ds-stroke-secondary",
      },
    },
  },
  defaultVariants: {
    status: "inactive",
  },
});
